// YTRepliesConsoleCard — the "operating console" below the locked YTRepliesCard.
// Feb 2026 (Randy pt-yt-console-tabs) — rewritten with a 4-tab layout so the
// mobile experience feels like a real app, not a scrolling accordion pile.
// Tabs: Inbox (drafts) · Settings (safety / filter / signature) · Analytics · Danger.
//
// Wiring changes vs prior version:
//   • Empty state instead of fake sample fallback (only shows samples when
//     locked or when siloId not provided — i.e. true preview mode).
//   • Listens for the `yt-integration-updated` window event dispatched by
//     YTRepliesSetupModal after a successful OAuth so the header flips to
//     "Connected as [Channel]" without a page reload.
//   • Refreshes on tab focus so quick-tab-away-and-back shows fresh drafts.
import { useState, useEffect, useCallback, useRef } from "react";
import {
  YoutubeLogo, ArrowClockwise, LockKey, Tray, SlidersHorizontal, ShieldWarning,
  ChartLineUp, Pause, Plugs, ArrowRight, MagnifyingGlass, CheckCircle, Shovel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SLOT_META, SAMPLE_DRAFTS, SAMPLE_STATS } from "@/lib/ytRepliesSample";
import { useEntitlements } from "@/lib/entitlements";
import { useAnchoredScrollTarget } from "@/hooks/useAnchoredCollapse";
import { ytApi } from "@/lib/ytApi";
import { api } from "@/lib/api";
import YTDraftCard from "@/components/YTDraftCard";
import DigRig from "@/components/DigRig";
import YTVideoPickerModal from "@/components/YTVideoPickerModal";
import YTBuildAsYouGoTab from "@/components/YTBuildAsYouGoTab";

// Feb 2026 — Map a raw draft row (backend shape) to the shape YTDraftCard
// expects (camelCase). Kept as a module-level helper so pending drafts,
// posted-today rows, and History rows all use the same transform.
function mapDraftRow(d) {
  return {
    id: d.id,
    videoId: d.video_id || "",
    videoTitle: d.video_title || "",
    videoTimeAgo: (d.comment_published_at || d.posted_at || "").slice(0, 16).replace("T", " "),
    author: d.author_display_name || "@viewer",
    authorInitials: (d.author_display_name || "??").replace("@", "").slice(0, 2).toUpperCase(),
    commentText: d.comment_text || "",
    draftReply: d.posted_text || d.draft_reply,
    skippedReason: d.skipped_reason,
    llmCost: d.llm_cost_usd ?? 0,
    confidence: d.confidence ?? 0,
    status: d.status || "pending",
    isQuestion: d.is_question === true || d.is_question === false ? d.is_question : null,
    // Feb 2026 (Randy pt-yt-reply-to-reply) — depth-2 (reply-to-reply)
    // context. When depth === 2, YTDraftCard renders the ↳ badge, the
    // Q2/NQ2 tag variant, and a muted quote of the parent (our AI) reply
    // so Randy can see the follow-up context at a glance.
    depth: typeof d.depth === "number" ? d.depth : 1,
    parentCommentId: d.parent_comment_id || null,
    parentReplyText: d.parent_reply_text || null,
    // Oct 2026 (Randy pt-yt-thread-limit) — a canned hand-off rather than a
    // drafted answer. Badged in the inbox; re-drafting it grants one more turn.
    kind: d.kind || null,
    // Oct 2026 (Randy) — this card is showing HIS existing YouTube reply,
    // pulled in by "Has this been answered?". Sending overwrites it.
    answeredElsewhere: !!d.answered_elsewhere,
    answeredElsewhereAt: d.answered_elsewhere_at || null,
  };
}

const SAFETY_MODES = [
  { v: "draft", label: "Draft-only", hint: "Every reply lands in this inbox — nothing posts without you." },
  { v: "full_auto_correction", label: "Full auto, with correction", hint: "Posts don't self-clear. Marked SENT with an option to re-draft or clear. Re-sending edits the existing post." },
  { v: "full_auto", label: "Full auto immediately", hint: "Clears after posting. Full confidence. Rate-limited to 1 reply per comment, spam filter on." },
];

const FILTER_MODES = [
  { v: "all", label: "All videos on this channel" },
  { v: "selected", label: "Only videos I select" },
  { v: "off", label: "No videos (turn off)" },
];

const TABS = [
  { v: "inbox", label: "Inbox", Icon: Tray },
  { v: "settings", label: "Settings", Icon: SlidersHorizontal },
  { v: "analytics", label: "Analytics", Icon: ChartLineUp },
  // Jun 2026 (Randy) — Build as you go: comments landing on videos that
  // aren't in the silo yet. See FEATURE_BUILD_AS_YOU_GO_SPEC.md.
  { v: "bayg", label: "Build as you go", Icon: Shovel },
  { v: "danger", label: "Danger", Icon: ShieldWarning },
];

export default function YTRepliesConsoleCard({ channelSlot = "channel1", siloId = null, siloSlug = null, pairMode = false, onStatusChange = null }) {
  const slot = SLOT_META[channelSlot] || SLOT_META.channel1;
  // Jul 30, 2026 (Randy pt89c-xxxviii) — pairMode = true means this card
  // is being rendered inside a YTRepliesConsolePair wrapper (Chats tab).
  // The wrapper owns the outer border + top channel-tab header + shared
  // collapse chevron. This card SKIPS its own outer wrapper + header +
  // internal collapse toggle when pairMode is on. Everything else
  // (sub-tabs, pills, content) renders identically.
  // Feb 2026 (Randy pt-yt-collapse-default) — YT Replies Console cards
  // default to COLLAPSED platform-wide (safeguard: no accidental scanning
  // through the console before you've opened it deliberately). Persisted
  // per-user, per-silo via /me/ui-prefs so once the user opens or closes
  // it, that choice sticks across sessions, browsers, and devices.
  const collapsePrefKey = `yt_console_card_${channelSlot}_collapsed`;
  const [collapsed, setCollapsed] = useState(true);
  const [collapsedPrefLoaded, setCollapsedPrefLoaded] = useState(false);
  // Iter160t (platform chevron sweep) — anchor-scroll compensation so
  // clicking "Expand ▾ / Collapse ▴" doesn't jump the page. See
  // /app/frontend/src/hooks/useAnchoredCollapse.js.
  const { cardRef, withAnchor } = useAnchoredScrollTarget();
  useEffect(() => {
    if (!siloId || pairMode) { setCollapsedPrefLoaded(true); return; }
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/ui-prefs", { params: { silo_id: siloId } });
        if (!cancel) {
          const v = (r.data?.prefs || {})[collapsePrefKey];
          setCollapsed(v === false ? false : true);
        }
      } catch { /* default stays collapsed */ }
      finally { if (!cancel) setCollapsedPrefLoaded(true); }
    })();
    return () => { cancel = true; };
  }, [siloId, collapsePrefKey, pairMode]);
  const toggleCollapsed = async () => {
    const next = !collapsed;
    withAnchor(() => setCollapsed(next));
    if (!siloId || pairMode) return;
    try { await api.patch("/me/ui-prefs", { silo_id: siloId, key: collapsePrefKey, value: next }); } catch { /* silent — visual already flipped */ }
  };
  const [activeTab, setActiveTab] = useState("inbox");
  // Jun 2026 (Randy) — unanswered comments sitting on videos that aren't in
  // the silo yet. Badged on the tab label so a hole is visible without opening
  // the tab. Refreshed by the tab itself, and once on mount (no YouTube quota:
  // /gaps reads the ledger + integration only).
  const [baygCount, setBaygCount] = useState(0);
  const [safety, setSafety] = useState("draft");
  const [videoFilter, setVideoFilter] = useState("all");
  // Feb 2026 (Randy pt-yt-conf-gate) — safety net for auto-modes. Toggle
  // defaults ON and threshold defaults 0.85 so newly-created integrations
  // don't accidentally auto-post low-confidence drafts.
  const [confGateEnabled, setConfGateEnabled] = useState(true);
  const [confGateThreshold, setConfGateThreshold] = useState(0.85);
  // Feb 2026 (Randy pt-yt-post-all) — result banner from the last [Post/
  // Accept ALL] run. Displays skip-reason counters in the inbox toolbar
  // area (in-DOM, not a toast) so the creator can see WHY things didn't
  // post and act on it. Cleared by user tap or by a successful re-post
  // of everything.
  const [postAllResult, setPostAllResult] = useState(null);
  // Feb 2026 (Randy pt-yt-caught-up + pt-yt-dig-depth) — cumulative
  // session batch counter, "reached end of channel history" pill, AND
  // cumulative "dig depth" (threads scanned since the cursor last reset).
  // sessionScanned lives on the integration row so it survives page
  // reloads — creators can walk away for a day and come back knowing
  // exactly how deep they've dug. Reset naturally when the cursor
  // resets (reached_end, ↺ Restart, monitored-list change, stale token).
  const [sessionBatched, setSessionBatched] = useState(0);
  const [sessionScanned, setSessionScanned] = useState(0);
  // Sep 2026 (Randy pt-yt-restart-depth) — where the last click STARTED, so
  // the readout shows the window it actually walked ("4,900–5,400 threads
  // deep") instead of a bare cumulative number.
  const [sessionScannedFrom, setSessionScannedFrom] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);
  // Oct 17, 2026 (Randy) — the dig map: real dates for the core-sample bar,
  // plus the silo↔channel pairing state that gates drafting.
  const [digMap, setDigMap] = useState(null);
  const [digMenuOpen, setDigMenuOpen] = useState(false);
  const [digLive, setDigLive] = useState("");
  const [signature, setSignature] = useState("— Answered by {silo_name}'s AI Silo · {silo_url}");
  const { owns } = useEntitlements();
  const isUnlocked = owns("yt_ai_replies");
  const [integration, setIntegration] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [stats, setStats] = useState({ pending: 0, postedToday: 0, historyTotal: 0, clicksThisWeek: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const isConnected = !!integration?.youtube_channel_id;

  // Preview mode = unlocked feature but no silo context (e.g. rendered on
  // the marketing preview). Show samples so the card doesn't look barren.
  // Real mode with a real silo → empty means empty; show the empty-state.
  const showSamples = !isUnlocked || !siloId;

  const refresh = useCallback(async () => {
    if (showSamples) {
      setDrafts(SAMPLE_DRAFTS[channelSlot] || []);
      setStats(SAMPLE_STATS[channelSlot] || { pending: 0, postedToday: 0, clicksThisWeek: 0 });
      setInitialLoaded(true);
      return;
    }
    setLoading(true);
    try {
      const [row, inboxRes] = await Promise.all([
        ytApi.getIntegration(siloId, channelSlot).catch(() => null),
        ytApi.listDrafts(siloId, channelSlot, "pending").catch(() => ({ drafts: [], stats: {} })),
      ]);
      setIntegration(row);
      // Oct 17, 2026 (Randy) — the dig map feeds the core-sample bar and the
      // pairing banner. Free: reads the integration row + local drafts, zero
      // YouTube quota, so it can ride along with every console refresh.
      if (row?.youtube_channel_id) {
        ytApi.digMap(siloId, channelSlot)
          .then((m) => setDigMap(m))
          .catch(() => { /* bar falls back to its empty state */ });
      }
      // Jun 2026 (Randy) — badge the Build-as-you-go tab without opening it.
      // Free: /gaps reads the ledger + integration row, zero YouTube quota.
      if (row?.youtube_channel_id) {
        ytApi.coverageGaps(siloId, channelSlot)
          .then((g) => setBaygCount(Number(g?.queued_comments) || 0))
          .catch(() => { /* badge stays hidden */ });
      }
      if (row) {
        setSafety(row.safety_mode || "draft");
        setVideoFilter(row.video_filter_mode || "all");
        setSignature(row.reply_signature || "— Answered by {silo_name}'s AI Silo · {silo_url}");
        // Feb 2026 (Randy pt-yt-dig-depth) — hydrate the cumulative
        // "threads scanned" counter from the integration row so the
        // "Digging at ~N threads deep" indicator is accurate on
        // first paint (page reload / tab switch / next-day return).
        setSessionScanned(Number(row.session_scanned) || 0);
        setSessionScannedFrom(Number(row.session_scanned_from) || 0);
        // Feb 2026 (Randy pt-yt-conf-gate) — hydrate the safety-net toggle
        // + threshold. Legacy rows without these fields fall back to the
        // (true, 0.85) defaults that the backend also uses at read time.
        setConfGateEnabled(row.confidence_gate_enabled !== false);
        setConfGateThreshold(
          typeof row.confidence_gate_threshold === "number" ? row.confidence_gate_threshold : 0.30
        );
      }
      // Jul 30, 2026 (Randy pt89c-xxxviii) — publish integration status
      // to a parent pair wrapper (if any) so it can render "Connected as
      // East Tactics" in the channel-tab label.
      if (onStatusChange) {
        try {
          onStatusChange({
            channelSlot,
            connected: !!row?.youtube_channel_id,
            channelName: row?.youtube_channel_name || null,
            paused: !!row?.paused,
          });
        } catch { /* silent */ }
      }
      const mapped = (inboxRes.drafts || []).map(mapDraftRow);
      setDrafts(mapped);
      setStats((prev) => ({
        pending: inboxRes.stats?.pending ?? 0,
        postedToday: inboxRes.stats?.posted_today ?? 0,
        // Feb 2026 (Randy pt-yt-history-count) — real lifetime terminal-
        // state count from the backend. Was previously hardcoded to 0
        // (via clicksThisWeek) which is why the History top-card always
        // showed 0 even with 138+ posted rows in the DB.
        historyTotal: inboxRes.stats?.history_total ?? 0,
        // Sep 2026 — how many pending cards the backend just retired because
        // he had already answered them on YouTube. Surfaced in the inbox so
        // the count dropping never looks like drafts going missing. Sticky
        // until dismissed: the verification is throttled, so the very next
        // refresh legitimately reports 0 and would otherwise erase the note
        // before he ever saw it.
        autoRetired: (inboxRes.stats?.auto_retired || 0) || prev.autoRetired || 0,
        clicksThisWeek: 0,
      }));
      lastRefreshAt.current = Date.now();
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, [showSamples, siloId, channelSlot]);

  useEffect(() => { refresh(); }, [refresh]);

  // Iter160w (Randy pt-yt-refresh-cadence, Feb 2026) — the focus refresh
  // path used to fire on EVERY tab focus, hitting getIntegration +
  // listDrafts back-to-back. During a long editing session Randy tab-
  // switches ~50-100× (YTRE, video, Workbench, etc.) — that's a lot of
  // pointless backend chatter for zero material state change. Now we
  // throttle to at most one focus-triggered refresh every 30 seconds.
  // The `yt-integration-updated` broadcast event (fired after a real
  // credential save / OAuth completion) still forces an immediate
  // refresh — it's the ONE path where fresh data actually matters.
  const FOCUS_REFRESH_THROTTLE_MS = 30_000;
  const lastRefreshAt = useRef(0);

  // Live-refresh when Setup Modal completes OAuth (broadcast event) or when
  // the user tabs away and back. No more stale "Not connected" after connect.
  useEffect(() => {
    if (showSamples) return;
    const onUpdated = (ev) => {
      if (!ev?.detail) { refresh(); return; }
      if (ev.detail.siloId === siloId && ev.detail.slot === channelSlot) refresh();
    };
    const onFocus = () => {
      if (Date.now() - lastRefreshAt.current < FOCUS_REFRESH_THROTTLE_MS) return;
      refresh();
    };
    window.addEventListener("yt-integration-updated", onUpdated);
    window.addEventListener("focus", onFocus);
    // Feb 17, 2026 (Randy pt-strike-live-sync-console) — also refresh when
    // a strike-through flips somewhere in the YT Tool library table (or
    // via `ensureVideoStruckByUrl` after a "Run selected Macro" pass).
    // The backend syncs `monitored_video_ids` server-side; this listener
    // pulls the new list so the "Only videos I select (N)" count on the
    // Video Monitor pill increments live — no page refresh needed.
    const onStrike = (ev) => {
      if (!ev?.detail) return;
      if (ev.detail.silo_id && ev.detail.silo_id !== siloId) return;
      refresh();
    };
    window.addEventListener("mysilo:yt-strike-changed", onStrike);
    return () => {
      window.removeEventListener("yt-integration-updated", onUpdated);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("mysilo:yt-strike-changed", onStrike);
    };
  }, [refresh, showSamples, siloId, channelSlot]);

  const pollNow = async () => {
    if (!isUnlocked || !siloId) return;
    if (!isConnected) return toast.info("Connect your YouTube channel via the locked-card setup pop-up first.");
    setBusy(true);
    try {
      const r = await ytApi.poll(siloId, channelSlot);
      const base = `Fetched — ${r.drafted || 0} new draft${r.drafted === 1 ? "" : "s"}.`;
      if (r?.note && (r.drafted || 0) === 0) toast.info(`${base}  ${r.note}`);
      else toast.success(base);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Poll failed.");
    } finally { setBusy(false); }
  };

  // Jul 30, 2026 (Randy pt89c-xxxii) — deeper backfill of unresponded
  // comments. YT commentThreads.list only surfaces the ~25 most recent
  // threads per cycle to the 90s poller. This pulls up to ~250 threads
  // back, skips already-replied threads, drafts everything else.
  // Feb 2026 (Randy pt-yt-post-all) — the auto-post opt-in prompt on
  // Pull unresponded is retired (Option Y from the RCA). Pull always
  // drafts as pending now; the new [Post/Accept ALL] button is the
  // single, always-visible way to bulk-post. Cleaner mental model,
  // one button = one job.
  // Feb 2026 (Randy pt-yt-caught-up) — tracks cumulative session batched
  // and the reached_end signal so the UI can tell creators honestly
  // when they've walked to the bottom of their channel history.
  const pullUnresponded = async (opts = {}) => {
    if (!isUnlocked || !siloId) return;
    if (!isConnected) return toast.info("Connect your YouTube channel first.");
    const isRestart = !!opts.resetCursor;
    // Oct 17, 2026 (Randy) — the browser confirm() is gone. On a phone it was
    // a wall of text over the whole screen, and the dig is a routine action,
    // not a destructive one. Restarting especially: "every time they bring in
    // a new video there's sort of a need almost to start the dig over." The
    // button's own label and the core-sample bar now carry the explanation,
    // and the result lands in the button instead of a confirm-then-toast.
    setBusy(true);
    setDigLive(isRestart ? "Restarting from your newest…" : "Digging deeper…");
    try {
      const r = await ytApi.backfill(siloId, channelSlot, {
        autoPost: false,
        resetCursor: isRestart,
        acknowledgePairing: !!opts.acknowledgePairing,
      });
      const newDrafts = r.drafted || 0;
      const alreadyHandled = r.skipped_replied || 0;
      const scanned = r.scanned || 0;
      // On explicit restart, don't ADD to session counter (fresh session).
      if (isRestart) {
        setSessionBatched(newDrafts);
      } else {
        setSessionBatched((prev) => prev + newDrafts);
      }
      setSessionScanned(Number(r.session_scanned) || 0);
      setSessionScannedFrom(Number(r.session_scanned_from) || 0);
      setReachedEnd(!!r.reached_end);
      // Feb 2026 (Randy pt-yt-skipped-replied-surface) — surface the
      // already-handled count so creators SEE where the ~497 other
      // threads went. Otherwise a "3 new drafts" toast on a 500-thread
      // scan looks like the button barely did anything.
      // Feb 2026 (Randy pt-yt-dig-depth-honesty, iter162) — when the
      // backend early-returns with `scanned=0` because the video
      // filter is "off" or "selected but empty", it puts the truthful
      // reason in `r.note`. Previous fallback branch here IGNORED that
      // note and always said "Keep clicking to walk deeper" even when
      // walking deeper wouldn't help. Now: any 0-scanned response
      // with a `note` surfaces the note as the toast — telling the
      // creator the real reason instead of stranding them clicking.
      const digDepthSuffix = (Number(r.session_scanned) || 0) > 0
        ? ` · threads ${(Number(r.session_scanned_from) || 0).toLocaleString()}–${(Number(r.session_scanned) || 0).toLocaleString()} deep this click.`
        : "";
      const msg = newDrafts > 0
        ? `${newDrafts} new draft${newDrafts === 1 ? "" : "s"} · ${alreadyHandled} already handled (of ${scanned} scanned).${digDepthSuffix}`
        : (r.cursor_reset
            ? (r.note || "Saved scan position reset — click again for a fresh sweep.")
            : (r.reached_end
                ? `You're all caught up! ${scanned} thread${scanned === 1 ? "" : "s"} scanned this batch, all already answered. No older unresponded comments remain.`
                : (scanned === 0 && r.note
                    ? r.note
                    : `0 new drafts this batch · ${alreadyHandled} already handled (of ${scanned} scanned).${digDepthSuffix} Keep clicking to walk deeper.`)));
      if (newDrafts > 0) toast.success(msg);
      else toast.info(msg);
      // The result also stays ON the button for a beat — the toast is the
      // fleeting copy, this is the one a creator on a phone actually reads.
      const era = r.era_oldest ? ` · reached ${String(r.era_oldest).slice(0, 7)}` : "";
      setDigLive(newDrafts > 0
        ? `${newDrafts} draft${newDrafts === 1 ? "" : "s"} found${era}`
        : (r.reached_end ? "Fully walked — next dig starts from newest" : `Nothing new${era}`));
      setTimeout(() => setDigLive(""), 6000);
      refresh();
    } catch (e) {
      // Oct 17, 2026 (Randy) — the pairing guard answers 409 with the two
      // channel names. Offer the dig again, once, as a deliberate act.
      const data = e.response?.data?.detail;
      if (e.response?.status === 409 && data?.code === "CHANNEL_PAIRING_MISMATCH") {
        setDigLive("");
        toast.error(data.message, {
          duration: 12000,
          action: {
            label: "Dig anyway",
            onClick: () => pullUnresponded({ ...opts, acknowledgePairing: true }),
          },
        });
        refresh();
        return;
      }
      setDigLive("");
      toast.error(typeof data === "string" ? data : (data?.message || "Backfill failed."));
    } finally { setBusy(false); }
  };

  // Feb 2026 (Randy pt-yt-post-all) — bulk-post every pending draft in
  // the inbox. Result renders as an in-DOM banner (skip-reason telemetry)
  // rather than a fleeting toast, so daily-cap / spacing / HTTP-error
  // reasons stick around long enough to be actionable. Result banner
  // lives in the InboxTab component (see `postAllResult` state below).
  // Feb 2026 (Randy pt-yt-trickle) — Post ALL is now a background trickle
  // job. Endpoint returns `{ job_id, total_pending }` immediately and the
  // real work runs in a Python asyncio task that sleeps `min_spacing`
  // seconds between posts (matching YouTube's spam-filter-friendly
  // cadence). The banner below polls `postAllJobLatest` every 2s and
  // updates in place — so creators see "Posting 3 of 48 · 44 remaining"
  // walking forward in real time.
  const postAll = async () => {
    if (!isUnlocked || !siloId) return;
    if (!isConnected) return toast.info("Connect your YouTube channel first.");
    const pendingCount = drafts.filter((d) => d.status === "pending").length;
    const isDraftMode = safety === "draft";
    const verb = isDraftMode ? "Accept & post" : "Post";
    if (!confirm(`${verb} ALL ${pendingCount} pending draft${pendingCount === 1 ? "" : "s"} to YouTube? Runs in the background — trickle-paces one post every ${integration?.min_spacing_seconds ?? 5}s (safely under YouTube's spam threshold). Daily cap${safety !== "draft" ? " + confidence gate" : ""} + per-comment cap all enforced. Anything skipped stays pending for next time.`)) return;
    setBusy(true);
    try {
      const r = await ytApi.postAll(siloId, channelSlot);
      if (r.job_id) {
        // Fetch the freshly-created job doc so the banner appears
        // immediately (polling loop will keep it updated).
        try {
          const j = await ytApi.postAllJobLatest(siloId, channelSlot);
          if (j?.job) setPostAllResult(j.job);
        } catch { /* poll effect will catch up */ }
      } else if (r.total_pending === 0) {
        toast.info("Inbox is empty — nothing to post.");
      }
      refresh();
    } catch (e) {
      // 409 = already-running job. Try to reattach to it.
      if (e.response?.status === 409) {
        try {
          const j = await ytApi.postAllJobLatest(siloId, channelSlot);
          if (j?.job) {
            setPostAllResult(j.job);
            toast.info("A Post ALL job is already running — attached to its progress.");
          }
        } catch { /* fall through */ }
      } else {
        toast.error(e.response?.data?.detail || "Post ALL failed.");
      }
    } finally { setBusy(false); }
  };

  // Feb 2026 (Randy pt-yt-approve-all) — bulk-approve every SENT draft.
  // Fast (no YouTube API calls), so no background job needed. Confirms
  // first so a stray click doesn't move dozens of things out of the
  // inbox unexpectedly.
  const approveAll = async () => {
    if (!isUnlocked || !siloId) return;
    if (!isConnected) return toast.info("Connect your YouTube channel first.");
    const sentCount = drafts.filter((d) => d.status === "sent").length;
    if (sentCount === 0) return toast.info("No sent drafts awaiting approval.");
    if (!confirm(`Approve ALL ${sentCount} sent draft${sentCount === 1 ? "" : "s"}? They'll close their correction windows and move to Posted. Their replies are already live on YouTube — this just marks them as creator-approved.`)) return;
    setBusy(true);
    try {
      const r = await ytApi.approveAll(siloId, channelSlot);
      toast.success(r.note || `${r.approved || 0} approved.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve ALL failed.");
    } finally { setBusy(false); }
  };

  // Feb 2026 (Randy pt-yt-trickle) — job polling loop + on-mount recovery.
  // Always polls every 2.5s while this integration card is mounted.
  // The `latest` endpoint respects the `dismissed` flag so once the
  // creator closes the banner it stays closed until the next Post ALL.
  // Uses a ref to detect the running → done transition without stale-
  // closure headaches, and fires one `refresh()` so posted rows leave
  // Iter160w (Randy pt-yt-refresh-cadence, Feb 2026) — this poller used
  // to hit /post-all-job/latest every 2.5s FOREVER while the card was
  // expanded + connected, even when no batch had ever run. During long
  // editing sessions that's ~24 requests/minute for zero material change.
  // Adaptive cadence:
  //   • While a job IS running → 2.5s (tight, so "batch done" refresh
  //     lands within a couple of seconds, which is the whole point).
  //   • While no job is running → 30s (idle heartbeat — we still need
  //     SOME cadence in case a batch was launched from another device,
  //     but 30s is plenty for a job that averages 30-90 seconds).
  // Result: ~90% fewer backend hits in a typical session.
  const postAllResultRef = useRef(postAllResult);
  useEffect(() => { postAllResultRef.current = postAllResult; }, [postAllResult]);
  useEffect(() => {
    if (!isUnlocked || !siloId || !isConnected) return;
    let cancelled = false;
    let timerId = null;
    const IDLE_MS = 30_000;
    const RUNNING_MS = 2_500;
    const fetchOnce = async () => {
      try {
        const j = await ytApi.postAllJobLatest(siloId, channelSlot);
        if (cancelled) return;
        if (j?.job) {
          const prev = postAllResultRef.current;
          if (prev?.status === "running" && j.job.status !== "running") {
            refresh();
          }
          setPostAllResult(j.job);
        } else {
          setPostAllResult(null);
        }
      } catch { /* transient — try again on next tick */ }
      if (cancelled) return;
      const nextDelay = postAllResultRef.current?.status === "running" ? RUNNING_MS : IDLE_MS;
      timerId = setTimeout(fetchOnce, nextDelay);
    };
    fetchOnce();
    return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
  }, [isUnlocked, siloId, channelSlot, isConnected, refresh]);

  // Jul 30, 2026 (Randy pt89c-xxxiii) — Clear inbox = hard-delete pending
  // + sent + skipped local rows so the next refresh re-drafts them from
  // scratch. Sep 17 2026: REJECTED rows now survive, so a comment you
  // decided not to answer can never come back.
  const clearInbox = async () => {
    if (!isUnlocked || !siloId) return;
    if (!confirm("Clear inbox? This DELETES all pending / sent / skipped drafts locally so the next Refresh re-drafts them fresh. Rejected comments are KEPT (they never come back), and posted (live) replies are NOT affected.")) return;
    setBusy(true);
    try {
      const r = await ytApi.clearInbox(siloId, channelSlot);
      toast.success(
        `Inbox cleared · ${r.deleted || 0} row${r.deleted === 1 ? "" : "s"} removed`
        + (r.kept_rejected ? ` · ${r.kept_rejected} rejection${r.kept_rejected === 1 ? "" : "s"} kept` : "")
        + ". Tap Refresh or Draft unresponded to re-draft."
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed.");
    } finally { setBusy(false); }
  };

  // Oct 2026 (LINK_LIBRARY_AUTOFIX_PROJECT.md · Phase A4) — re-draft the group
  // the creator is looking at. Randy: *"it keeps the inbox up, but simply
  // re-drafts them all."* The ids come from the Inbox's own filtered list, so
  // whatever the Type filter shows is exactly what gets re-written. Runs as a
  // background job (a reply takes seconds of model time, twenty of them would
  // outlast any request), and the first one through re-checks the Link Library,
  // which is why this is the honest answer to "my drafts have ugly links".
  const [redraftJob, setRedraftJob] = useState(null);
  const redraftPollRef = useRef(null);

  const pollRedraftJob = () => {
    clearTimeout(redraftPollRef.current);
    const tick = async () => {
      try {
        const { job } = await ytApi.redraftJobLatest(siloId, channelSlot);
        setRedraftJob(job || null);
        if (job && job.status === "running") {
          redraftPollRef.current = setTimeout(tick, 2000);
        } else if (job) {
          refresh();
          toast.success(job.note || "Re-draft finished.");
        }
      } catch { /* banner is cosmetic — a missed poll is not an error */ }
    };
    redraftPollRef.current = setTimeout(tick, 1200);
  };
  useEffect(() => () => clearTimeout(redraftPollRef.current), []);

  const redraftGroup = async (ids) => {
    if (!isUnlocked || !siloId) return;
    const n = (ids || []).length;
    if (!n) { toast.message("Nothing listed to re-draft."); return; }
    if (!confirm(
      `Re-draft ${n} repl${n === 1 ? "y" : "ies"}?\n\n`
      + "Each one is written again from your silo as it stands now — including any "
      + "link names you have fixed since. This spends model credit per reply. "
      + "Anything you have already posted stays live until you update it."
    )) return;
    try {
      const r = await ytApi.redraftGroup(siloId, channelSlot, ids);
      if (!r.job_id) { toast.message(r.note || "Nothing to re-draft."); return; }
      setRedraftJob({ status: "running", total: r.total, redrafted: 0, note: `Re-drafting ${r.total}…` });
      pollRedraftJob();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Re-draft failed to start.");
    }
  };

  // Jul 30, 2026 (Randy pt89c-xxxiii) — lazy one-shot metadata backfill.
  // Fires the first time this integration loads in a session so existing
  // rows get their is_question + video_title filled in. Idempotent
  // server-side; safe to call multiple times.
  useEffect(() => {
    if (showSamples || !siloId || !isConnected || !isUnlocked) return;
    let cancelled = false;
    ytApi.backfillMetadata(siloId, channelSlot)
      .then((r) => {
        if (cancelled) return;
        const changed = (r.is_question_backfilled || 0) + (r.video_title_backfilled || 0);
        if (changed > 0) refresh();
      })
      .catch(() => { /* silent — non-critical */ });
    return () => { cancelled = true; };
  }, [siloId, channelSlot, isConnected, isUnlocked]);

  const doApprove = async (id) => {
    // Kept for backward compat with any inline callers, but new DraftCard
    // handles approve internally + opens the Append-Q&A CTA.
    setBusy(true);
    try { await ytApi.approveDraft(id); toast.success("Reply posted to YouTube."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed."); }
    finally { setBusy(false); }
  };
  const doTogglePause = async () => {
    setBusy(true);
    try { const r = await ytApi.togglePause(siloId, channelSlot); toast(r.paused ? "Paused — no new posts." : "Unpaused."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };
  const doDisconnect = async () => {
    if (!confirm("Disconnect YouTube from this slot? This clears the OAuth token — your saved Google Cloud credentials AND full draft history stay preserved. Reconnecting on this silo restores everything.")) return;
    setBusy(true);
    try { await ytApi.disconnect(siloId, channelSlot); toast.success("YouTube disconnected · history preserved."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };

  const persistSetting = async (patch) => {
    if (!isUnlocked || !siloId) return;
    try { await ytApi.patchIntegration(siloId, channelSlot, patch); toast.success("Saved."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    if (onStatusChange) {
      try { onStatusChange({ channelSlot, connected: false, channelName: null, paused: false }); } catch { /* silent */ }
    }
  };

  return (
    <div
      ref={cardRef}
      className={pairMode ? "" : "mt-3 border hairline rounded-2xl bg-[#FDFCF8] shadow-sm overflow-hidden"}
      data-testid={`yt-replies-console-${channelSlot}`}
    >
      {/* Header — connection + collapse. HIDDEN in pairMode (the pair wrapper owns this). */}
      {!pairMode && (
      <div className="px-5 py-3 border-b hairline flex items-center justify-between gap-3 bg-white/40">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1 shrink-0">
            <YoutubeLogo size={16} weight="fill" className="text-[#B54848]" />
            <span className={`w-2 h-2 rounded-full ${isConnected ? "bg-[#2F6B3A]" : "bg-[#C16E5A]"}`} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B6A68] font-medium">Console · {slot.name} · {slot.where}</div>
            <div className="text-[13px] font-medium text-[#1C1B1A] flex items-center gap-2 flex-wrap">
              {isConnected ? (
                <>
                  <span>Connected as {integration.youtube_channel_name}</span>
                  {integration.paused && <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-[#FDECEC] text-[#B54848] uppercase tracking-[0.14em] font-medium">Paused</span>}
                </>
              ) : (
                <>
                  <span>Not connected</span>
                  {!isUnlocked && <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] uppercase tracking-[0.14em] font-normal">Preview mode</span>}
                </>
              )}
            </div>
          </div>
        </div>
        <button onClick={toggleCollapsed} data-testid={`yt-console-collapse-${channelSlot}`}
          className="text-[10px] uppercase tracking-[0.16em] text-[#4A5D4E] hover:text-[#1C1B1A] px-2 py-1">
          {collapsed ? "Expand ▾" : "Collapse ▴"}
        </button>
      </div>
      )}

      {/* Body — kept conditionally mounted so polling / API fetches in
          the tabs don't run while collapsed. Iter160t provides the
          anchor-scroll via withAnchor() in toggleCollapsed → page
          doesn't jump when the card resizes, even without an animated
          grid transition. */}
      {(pairMode || !collapsed) && (
        <>
          {/* Preview banner — only while locked */}
          {!isUnlocked && (
            <div className="mx-5 mt-4 bg-[#FDF4EE]/60 border border-[#EBC9C9] rounded-md p-3 text-[12px] text-[#8A4844] flex items-start gap-2">
              <LockKey size={13} weight="duotone" className="mt-0.5 shrink-0" />
              <span>Unlock the add-on above to activate this console. Everything you see below is a preview of what you&apos;ll see once your YouTube channel is connected.</span>
            </div>
          )}

          {/* Tab bar. Jun 2026 (Randy) — MOBILE: five tabs sharing a 390px
              row truncated every label to "Sett…", "Anal…", "B.", "Dan…".
              Below `sm` the row scrolls horizontally with full labels; from
              `sm` up it goes back to five equal columns. */}
          <div className="flex overflow-x-auto scrollbar-hide sm:overflow-visible border-b hairline bg-white/30 mt-1">
            {TABS.map((t) => {
              const isActive = activeTab === t.v;
              const badgeCount = t.v === "inbox"
                ? (stats.pending > 0 ? stats.pending : 0)
                : (t.v === "bayg" ? baygCount : 0);
              const badge = badgeCount > 0;
              return (
                <button
                  key={t.v}
                  onClick={() => setActiveTab(t.v)}
                  data-testid={`yt-console-tab-${t.v}-${channelSlot}`}
                  className={`shrink-0 sm:flex-1 sm:min-w-0 px-3 py-2.5 text-[11.5px] font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
                    isActive
                      ? "border-[#1C1B1A] text-[#1C1B1A] bg-white"
                      : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-white/60"
                  } ${t.v === "danger" ? (isActive ? "!text-[#B54848] !border-[#B54848]" : "hover:!text-[#B54848]") : ""}`}
                  aria-selected={isActive}
                >
                  <t.Icon size={13} weight="duotone" className="shrink-0" />
                  <span className="whitespace-nowrap sm:truncate">{t.label}</span>
                  {badge && (
                    <span className={`shrink-0 text-[9.5px] px-1.5 py-0.5 rounded-full ${isActive ? "bg-[#B54848] text-white" : "bg-[#F0F1EE] text-[#6B6A68]"}`}>
                      {badgeCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="p-5">
            {activeTab === "inbox" && (
              <InboxTab
                slot={slot}
                drafts={drafts}
                stats={stats}
                digMap={digMap}
                digLive={digLive}
                onOpenSettings={() => setActiveTab("settings")}
                busy={busy}
                loading={loading}
                showSamples={showSamples}
                isUnlocked={isUnlocked}
                isConnected={isConnected}
                initialLoaded={initialLoaded}
                pollNow={pollNow}
                pullUnresponded={pullUnresponded}
                redraftGroup={redraftGroup}
                redraftJob={redraftJob}
                clearRedraftJob={() => setRedraftJob(null)}
                clearInbox={clearInbox}
                postAll={postAll}
                approveAll={approveAll}
                postAllResult={postAllResult}
                clearPostAllResult={async () => {
                  // Feb 2026 (Randy pt-yt-trickle) — also hit the dismiss
                  // endpoint so the next poll doesn't re-attach to this
                  // completed job. Fire-and-forget: state is cleared
                  // locally either way.
                  const j = postAllResult;
                  setPostAllResult(null);
                  if (j?.id && j?.status !== "running") {
                    try { await ytApi.postAllJobDismiss(siloId, channelSlot, j.id); }
                    catch { /* fine — banner already gone locally */ }
                  }
                }}
                sessionBatched={sessionBatched}
                sessionScanned={sessionScanned}
                sessionScannedFrom={sessionScannedFrom}
                reachedEnd={reachedEnd}
                safety={safety}
                refresh={refresh}
                siloId={siloId}
                siloSlug={siloSlug}
                channelSlot={channelSlot}
                integration={integration}
              />
            )}

            {activeTab === "settings" && (
              <SettingsTab
                safety={safety} setSafety={setSafety}
                videoFilter={videoFilter} setVideoFilter={setVideoFilter}
                signature={signature} setSignature={setSignature}
                confGateEnabled={confGateEnabled} setConfGateEnabled={setConfGateEnabled}
                confGateThreshold={confGateThreshold} setConfGateThreshold={setConfGateThreshold}
                isUnlocked={isUnlocked} integration={integration}
                persistSetting={persistSetting}
                channelSlot={channelSlot}
                siloId={siloId}
              />
            )}

            {activeTab === "analytics" && (
              <AnalyticsTab stats={stats} />
            )}

            {activeTab === "bayg" && (
              <YTBuildAsYouGoTab
                siloId={siloId}
                channelSlot={channelSlot}
                isUnlocked={isUnlocked}
                isConnected={isConnected}
                onCount={setBaygCount}
              />
            )}

            {activeTab === "danger" && (
              <DangerTab
                integration={integration}
                busy={busy}
                isUnlocked={isUnlocked}
                doTogglePause={doTogglePause}
                doDisconnect={doDisconnect}
                channelSlot={channelSlot}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────

function InboxTab({ slot, drafts, stats, busy, loading, showSamples, isUnlocked, isConnected, initialLoaded, pollNow, pullUnresponded, redraftGroup, redraftJob, clearRedraftJob, clearInbox, postAll, approveAll, postAllResult, clearPostAllResult, sessionBatched, sessionScanned, sessionScannedFrom = 0, reachedEnd, safety, refresh, siloId, siloSlug, channelSlot, integration, digMap, digLive = "", onOpenSettings }) {
  // Jul 30, 2026 (Randy pt89c-xxxi) — 3 interactive pills. Each swaps the
  // main panel content: Pending drafts / Posted today / History browser.
  const [view, setView] = useState("pending"); // "pending" | "today" | "history"
  const [todayRows, setTodayRows] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  // Jul 30, 2026 (Randy pt89c-xxxii) — LOCAL-ONLY dismiss set for Posted
  // today + History rows. Resets whenever the pill view changes or the
  // History query re-runs (so hidden rows reappear on re-search).
  const [locallyDismissed, setLocallyDismissed] = useState(new Set());
  // Sep 2026 — one-click dismiss for the "already answered on YouTube" note.
  const [retiredDismissed, setRetiredDismissed] = useState(false);
  const dismissLocally = (id) => setLocallyDismissed((s) => { const n = new Set(s); n.add(id); return n; });
  const resetDismissed = () => setLocallyDismissed(new Set());
  useEffect(() => { resetDismissed(); }, [view]);

  // Jul 30, 2026 (Randy pt89c-xxxiii) — Pending-view controls:
  //   • qFilter: "all" | "q" | "nq" — filters visible pending drafts by
  //     the is_question flag stored on each row (backfilled automatically
  //     on session start via /backfill-metadata).
  //   • kw: keyword — classical live-highlight search. Matches inside
  //     comment_text + draft_reply + video_title + author.
  //   • matchCursor: which match is currently focused for the Prev/Next
  //     arrows (0-indexed).
  const [qFilter, setQFilter] = useState("all");
  const [kw, setKw] = useState("");
  const [matchCursor, setMatchCursor] = useState(0);
  const filteredDrafts = drafts.filter((d) => {
    if (qFilter === "q" && d.isQuestion !== true) return false;
    if (qFilter === "nq" && d.isQuestion !== false) return false;
    return true;
  });
  const kwLower = kw.trim().toLowerCase();
  // Rows that contain the keyword anywhere searchable
  const kwMatchIds = kwLower
    ? filteredDrafts
        .filter((d) => (
          (d.commentText || "").toLowerCase().includes(kwLower) ||
          (d.draftReply || "").toLowerCase().includes(kwLower) ||
          (d.videoTitle || "").toLowerCase().includes(kwLower) ||
          (d.author || "").toLowerCase().includes(kwLower)
        ))
        .map((d) => d.id)
    : [];
  useEffect(() => { setMatchCursor(0); }, [kw, qFilter]);
  useEffect(() => {
    if (view !== "today" || !siloId || !isUnlocked) return;
    let cancelled = false;
    setTodayLoading(true);
    ytApi.postedHistory(siloId, channelSlot, { range: "today", filter: "all" })
      .then((r) => { if (!cancelled) setTodayRows((r.drafts || []).map(mapDraftRow)); })
      .catch(() => { if (!cancelled) setTodayRows([]); })
      .finally(() => { if (!cancelled) setTodayLoading(false); });
    return () => { cancelled = true; };
  }, [view, siloId, channelSlot, isUnlocked]);

  const pills = [
    { id: "pending", label: "Pending", value: stats.pending, cls: "text-[#B54848]",
      titleHint: "Drafts awaiting your review (pending or sent-with-correction-window)." },
    // Feb 2026 (Randy pt-yt-24h-label + pt-yt-24h-tooltip) — was labeled
    // "Posted today" but the counter is actually a rolling 24-hour window
    // (matches YT's own quota model + prevents 200 AM / 200 PM burst
    // gaming). Renamed for honesty; tooltip explains the window.
    // Jun 2026 — `shortLabel` keeps the mobile card one line tall; the full
    // label wrapped to two lines in a ~110px card and made the three cards
    // different heights.
    { id: "today", label: "Posted (last 24h)", shortLabel: "Posted · 24h",
      value: stats.postedToday, cls: "text-[#2F6B3A]",
      titleHint: "Replies your channel has published in the last 24 hours. Rolls forward continuously — this is NOT a midnight-reset counter. Counts toward your daily post cap." },
    // Feb 2026 (Randy pt-yt-history-count) — was bound to `clicksThisWeek`
    // which isn't populated; card always showed 0 even with 138+ posted
    // rows. Now bound to `historyTotal` from the backend stats.
    { id: "history", label: "History", value: stats.historyTotal, cls: "text-[#2C6E92]",
      subLabel: "Search past replies",
      titleHint: "Lifetime count of replies your channel has posted through this integration (excludes still-pending)." },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2 text-center">
        {pills.map((s) => {
          const active = view === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setView(s.id)}
              data-testid={`yt-inbox-pill-${channelSlot}-${s.id}`}
              title={s.titleHint}
              className={`border hairline rounded-md px-1.5 py-2 sm:px-2 bg-white text-center transition flex flex-col items-center justify-center min-h-[62px] ${active ? "ring-2 ring-[#4A5D4E]/40 bg-[#F4F3EE]" : "hover:bg-[#FAFAF6]"}`}
            >
              <div className={`font-serif-display text-2xl leading-none ${s.cls}`}>{s.value}</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.16em] text-[#6B6A68] mt-1 leading-tight">
                <span className="sm:hidden">{s.shortLabel || s.label}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            </button>
          );
        })}
      </div>

      {stats.autoRetired > 0 && !retiredDismissed && (
        <div
          data-testid={`yt-auto-retired-note-${channelSlot}`}
          className="flex items-start gap-2 border hairline rounded-md bg-[#F4F3EE] px-3 py-2 text-[11px] leading-relaxed text-[#4A4A48]"
        >
          <CheckCircle size={13} weight="fill" className="mt-0.5 shrink-0 text-[#2F6B3A]" />
          <span>
            Checked against YouTube: <strong>{stats.autoRetired}</strong>{" "}
            {stats.autoRetired === 1 ? "draft was" : "drafts were"} already answered on your
            channel (or were your own comment), so they were retired instead of left
            waiting. Nothing was posted or deleted.
          </span>
          <button
            onClick={() => setRetiredDismissed(true)}
            data-testid={`yt-auto-retired-dismiss-${channelSlot}`}
            className="ml-auto shrink-0 text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] hover:text-[#2C2C2A]"
          >
            Dismiss
          </button>
        </div>
      )}

      {view === "pending" && (
        <div>
          {/* Toolbar row 1: title + refresh cluster */}
          {/* Oct 17, 2026 (Randy) — the title row keeps only its title. The
              three competing 10px word-links (Draft unresponded / Restart /
              Refresh) and the dig readout moved into <DigRig> below, which
              gives the dig one instrument with a real hierarchy. */}
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2 flex items-center justify-between gap-2 flex-wrap">
            <span>Pending drafts · {slot.name}{qFilter !== "all" ? ` · ${qFilter === "q" ? "Questions only" : "Non-questions only"}` : ""}</span>
            {reachedEnd && (
              <span
                data-testid={`yt-console-caught-up-${channelSlot}`}
                className="text-[10px] normal-case tracking-normal inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#F4F8F5] border border-[#B9D4BE] text-[#2F6B3A]"
                title="The last dig reached the end of this channel's comment history. No older unresponded comments exist right now."
              >
                ✓ All caught up
              </span>
            )}
          </div>

          <DigRig
            siloId={siloId}
            channelSlot={channelSlot}
            siloName={digMap?.silo_name || ""}
            integration={integration}
            digMap={digMap}
            busy={busy}
            loading={loading}
            isConnected={isConnected}
            isUnlocked={isUnlocked}
            digLive={digLive}
            reachedEnd={reachedEnd}
            sessionScanned={sessionScanned}
            sessionScannedFrom={sessionScannedFrom}
            onDig={() => pullUnresponded()}
            onRestart={() => pullUnresponded({ resetCursor: true })}
            onRefresh={pollNow}
            onResolved={refresh}
            onOpenSettings={onOpenSettings}
          />

          {/* Feb 2026 (Randy pt-yt-trickle) — Post ALL job banner.
              Renders live progress while `status: running` (updated
              every 2.5s by the job-polling useEffect above). Also
              persists the final result until the creator dismisses,
              so skip-reason counters stay actionable. Refreshed via
              the polling loop — no local edits here. */}
          {postAllResult && (
            <div
              data-testid={`yt-inbox-post-all-banner-${channelSlot}`}
              className={`mb-3 rounded-md border px-3 py-2 text-[12px] flex items-start gap-2 ${
                postAllResult.status === "failed"
                  ? "bg-[#FBEBE4] border-[#EBC7BB] text-[#8A4844]"
                  : postAllResult.status === "running"
                    ? "bg-[#EAF2FA] border-[#B7CCE0] text-[#1B4E6E]"
                    : (postAllResult.skipped_daily_cap || postAllResult.skipped_http_error)
                      ? "bg-[#FBEBE4] border-[#EBC7BB] text-[#8A4844]"
                      : "bg-[#F4F8F5] border-[#B9D4BE] text-[#2F6B3A]"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {postAllResult.status === "running" && (
                    <ArrowClockwise size={12} weight="bold" className="animate-spin shrink-0" data-testid={`yt-inbox-post-all-spin-${channelSlot}`} />
                  )}
                  {postAllResult.status === "running"
                    ? `Posting… ${postAllResult.posted || 0} of ${postAllResult.total_pending || 0}`
                    : postAllResult.status === "failed"
                      ? "Post ALL failed"
                      : (postAllResult.posted > 0
                        ? `Posted ${postAllResult.posted} of ${postAllResult.total_pending || 0}`
                        : `0 of ${postAllResult.total_pending || 0} posted`)}
                </div>
                {/* Progress bar — only while running. */}
                {postAllResult.status === "running" && (postAllResult.total_pending || 0) > 0 && (
                  <div
                    className="mt-1.5 h-1 rounded-full bg-[#DDE6EE] overflow-hidden"
                    data-testid={`yt-inbox-post-all-progress-${channelSlot}`}
                  >
                    <div
                      className="h-full bg-[#2C6E92] transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.round(((postAllResult.posted || 0) / (postAllResult.total_pending || 1)) * 100))}%` }}
                    />
                  </div>
                )}
                <div className="text-[11px] mt-0.5 opacity-90">{postAllResult.note}</div>
                {postAllResult.status === "running" && (
                  <div className="text-[10.5px] mt-0.5 opacity-75 italic">
                    Runs in the background — you can safely tab away and come back.
                  </div>
                )}
                {postAllResult.skipped_daily_cap > 0 && (
                  <div className="text-[11px] mt-1">
                    Daily post cap reached. Bump <strong>Max posts per day</strong> in the Rate Limits card below, or wait for the 24-hour window to roll forward.
                  </div>
                )}
                {postAllResult.skipped_http_error > 0 && (
                  <div className="text-[11px] mt-1">
                    YouTube API rejected {postAllResult.skipped_http_error} posts — check backend logs for quotaExceeded / permission / spam-flag reasons.
                  </div>
                )}
              </div>
              {/* Dismiss only when the job isn't running — the creator
                  should let it finish (or cancel it, which we don't
                  ship yet). */}
              {postAllResult.status !== "running" && (
                <button
                  onClick={clearPostAllResult}
                  data-testid={`yt-inbox-post-all-banner-dismiss-${channelSlot}`}
                  className="text-[16px] leading-none opacity-60 hover:opacity-100"
                  title="Dismiss"
                >×</button>
              )}
            </div>
          )}

          {/* Toolbar row 2 — Jul 30, 2026 (Randy pt89c-xxxiii): Filter dropdown + Clear inbox + classical Search.
              Feb 2026 (Randy pt-yt-post-all): Filter narrowed 50%, new [Post/Accept ALL] button between filter and clear.
              Only rendered when there's actually stuff to filter (or the search is active). */}
          {(drafts.length > 0 || kw) && (
            <div className="mb-3 flex items-center gap-2 flex-wrap bg-white/60 border hairline rounded-md px-2 py-1.5">
              {/* Feb 2026 (Randy pt-yt-filter-narrow-plus10) — 68px → 75px
                  (+10%) so "All (44)" reads comfortably. Label changed
                  from "FILTER:" to "TYPE:" per Randy — semantically
                  cleaner since the dropdown filters by Q/NQ type. */}
              <label className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68]">Type:</label>
              <select
                value={qFilter}
                onChange={(e) => setQFilter(e.target.value)}
                data-testid={`yt-inbox-qfilter-${channelSlot}`}
                className="text-[10.5px] px-1 py-1 border hairline rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] w-[75px] max-w-[75px]"
              >
                <option value="all">All ({drafts.length})</option>
                <option value="q">Qs ({drafts.filter((d) => d.isQuestion === true).length})</option>
                <option value="nq">NQs ({drafts.filter((d) => d.isQuestion === false).length})</option>
              </select>

              {/* Feb 2026 (Randy pt-yt-approve-all-split) — Post ALL and
                  Approve ALL are now separate CONTEXTUAL buttons rather
                  than one confused "Post ALL" that tried to do both.
                    • Post ALL      → creates new YT replies for PENDING drafts
                    • Approve ALL   → closes correction windows for SENT drafts
                  Each hides when its count is 0, so the toolbar is only
                  ever cluttered by actions that actually apply.
                  Text on Post ALL still swaps between "Post ALL" (auto
                  modes) and "Accept ALL" (draft mode) — same semantic
                  from before. */}
              {drafts.filter((d) => d.status === "pending").length > 0 && (
                <button
                  onClick={postAll}
                  disabled={busy || loading || !isUnlocked || !isConnected}
                  data-testid={`yt-inbox-post-all-${channelSlot}`}
                  title={safety === "draft"
                    ? "Accept ALL pending drafts — ship every one to YouTube. Rate-limit-aware."
                    : "Post ALL pending drafts — ship every one to YouTube. Trickle-paced, rate-limit-aware, confidence-gated."}
                  className="text-[10.5px] px-2 py-1 rounded border border-[#2F6B3A] bg-[#F4F8F5] text-[#2F6B3A] hover:bg-[#E8F1EA] disabled:opacity-40 inline-flex items-center gap-1 font-medium"
                >
                  <CheckCircle size={11} weight="fill" />
                  {safety === "draft" ? "Accept ALL" : "Post ALL"}
                  <span className="text-[9.5px] opacity-70">({drafts.filter((d) => d.status === "pending").length})</span>
                </button>
              )}

              {drafts.filter((d) => d.status === "sent").length > 0 && (
                <button
                  onClick={approveAll}
                  disabled={busy || loading || !isUnlocked || !isConnected}
                  data-testid={`yt-inbox-approve-all-${channelSlot}`}
                  title="Approve ALL sent drafts — closes the correction window for every one at once and moves them to Posted. No YouTube API calls (they're already live)."
                  className="text-[10.5px] px-2 py-1 rounded border border-[#2C6E92] bg-[#EAF2FA] text-[#1B4E6E] hover:bg-[#D9E7F2] disabled:opacity-40 inline-flex items-center gap-1 font-medium"
                >
                  <CheckCircle size={11} weight="fill" />
                  Approve ALL
                  <span className="text-[9.5px] opacity-70">({drafts.filter((d) => d.status === "sent").length})</span>
                </button>
              )}

              {/* Oct 2026 (LINK_LIBRARY_AUTOFIX_PROJECT.md · Phase A4) —
                  Re-draft this group. Randy: *"I believe the whole idea of
                  having a clear inbox button was to actually do just that —
                  re-draft everything, only the user would have to reset the
                  dig and start fresh."* This writes the listed replies again
                  in place, so the inbox stays up and no dig is paid for. */}
              <button
                onClick={() => redraftGroup(filteredDrafts.map((d) => d.id))}
                disabled={busy || loading || !isUnlocked || redraftJob?.status === "running" || filteredDrafts.length === 0}
                data-testid={`yt-inbox-redraft-group-${channelSlot}`}
                title="Re-draft this group — writes every reply listed below again, from your silo as it stands now (including link names you have fixed since). Keeps the inbox exactly as it is; no dig needed. Spends model credit per reply."
                className="text-[10.5px] px-2 py-1 rounded border hairline text-[#2C5D7C] hover:bg-[#EAF2FA] disabled:opacity-40 inline-flex items-center gap-1"
              >
                <ArrowClockwise size={11} weight="bold" className={redraftJob?.status === "running" ? "animate-spin" : ""} />
                Re-draft this group
                <span className="text-[9.5px] opacity-70">({filteredDrafts.length})</span>
              </button>

              {/* Classical search — live match highlight + prev/next arrows */}
              <div className="ml-auto flex items-center gap-1">
                <MagnifyingGlass size={11} weight="bold" className="text-[#6B6A68]" />
                <input
                  type="text"
                  placeholder="Search in view…"
                  value={kw}
                  onChange={(e) => setKw(e.target.value)}
                  data-testid={`yt-inbox-search-${channelSlot}`}
                  className="text-[11px] px-2 py-1 border hairline rounded bg-white w-40 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                />
                {kw && (
                  <>
                    <span className="text-[10px] text-[#6B6A68] tabular-nums">
                      {kwMatchIds.length === 0 ? "0 / 0" : `${matchCursor + 1} / ${kwMatchIds.length}`}
                    </span>
                    <button
                      onClick={() => setMatchCursor((c) => (kwMatchIds.length ? (c - 1 + kwMatchIds.length) % kwMatchIds.length : 0))}
                      disabled={kwMatchIds.length === 0}
                      data-testid={`yt-inbox-search-prev-${channelSlot}`}
                      className="text-[11px] w-6 h-6 rounded border hairline hover:bg-[#F0F1EE] disabled:opacity-30"
                      title="Previous match"
                    >↑</button>
                    <button
                      onClick={() => setMatchCursor((c) => (kwMatchIds.length ? (c + 1) % kwMatchIds.length : 0))}
                      disabled={kwMatchIds.length === 0}
                      data-testid={`yt-inbox-search-next-${channelSlot}`}
                      className="text-[11px] w-6 h-6 rounded border hairline hover:bg-[#F0F1EE] disabled:opacity-30"
                      title="Next match"
                    >↓</button>
                    <button
                      onClick={() => setKw("")}
                      data-testid={`yt-inbox-search-clear-${channelSlot}`}
                      className="text-[10px] text-[#6B6A68] hover:text-[#B54848]"
                      title="Clear search"
                    >×</button>
                  </>
                )}
                {/* Oct 2026 (Randy) — Clear inbox, demoted. It no longer has a
                    job the group re-draft does better, but it keeps two: it
                    bins sent/skipped clutter, and it lets the next dig
                    re-decide which comments qualify after a Type-filter change.
                    So it stays — quiet, at the end, out of the way. */}
                <button
                  onClick={clearInbox}
                  disabled={busy || loading || !isUnlocked}
                  data-testid={`yt-inbox-clear-${channelSlot}`}
                  title="Clear inbox — DELETES pending / sent / skipped local rows so the next dig re-drafts them from scratch. Rejected comments are KEPT, so anything you rejected never comes back. Use this to re-decide which comments qualify; to simply rewrite what is listed, use Re-draft this group."
                  className="ml-1 text-[10px] text-[#8A8884] hover:text-[#8A4844] underline underline-offset-2 decoration-dotted disabled:opacity-40"
                >
                  Clear inbox
                </button>
              </div>
            </div>
          )}

          {/* Oct 2026 (Phase A4) — re-draft batch banner. Same shape as the
              Post ALL banner above: live progress while running, final note
              until dismissed. Dismissal is local only — the job is already
              finished by then, and its result is the drafts themselves. */}
          {redraftJob && (
            <div
              data-testid={`yt-inbox-redraft-banner-${channelSlot}`}
              className={`mb-3 rounded-md border px-3 py-2 text-[12px] flex items-start gap-2 ${
                redraftJob.status === "failed"
                  ? "bg-[#FBEBE4] border-[#EBC7BB] text-[#8A4844]"
                  : redraftJob.status === "running"
                    ? "bg-[#EAF2FA] border-[#B7CCE0] text-[#1B4E6E]"
                    : "bg-[#F4F8F5] border-[#B9D4BE] text-[#2F6B3A]"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {redraftJob.status === "running" && (
                    <ArrowClockwise size={12} weight="bold" className="animate-spin shrink-0" data-testid={`yt-inbox-redraft-spin-${channelSlot}`} />
                  )}
                  {redraftJob.status === "running"
                    ? `Re-drafting… ${redraftJob.redrafted || 0} of ${redraftJob.total || 0}`
                    : redraftJob.status === "failed"
                      ? "Re-draft batch failed"
                      : `Re-drafted ${redraftJob.redrafted || 0} of ${redraftJob.total || 0}`}
                </div>
                {redraftJob.status === "running" && (redraftJob.total || 0) > 0 && (
                  <div className="mt-1.5 h-1 rounded-full bg-[#DDE6EE] overflow-hidden" data-testid={`yt-inbox-redraft-progress-${channelSlot}`}>
                    <div
                      className="h-full bg-[#2C6E92] transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.round(((redraftJob.redrafted || 0) / (redraftJob.total || 1)) * 100))}%` }}
                    />
                  </div>
                )}
                {redraftJob.note && <div className="text-[11px] mt-0.5 opacity-90">{redraftJob.note}</div>}
                {redraftJob.status === "running" && (
                  <div className="text-[10.5px] mt-0.5 opacity-75 italic">
                    Runs in the background — you can safely tab away and come back.
                  </div>
                )}
              </div>
              {redraftJob.status !== "running" && (
                <button
                  onClick={clearRedraftJob}
                  data-testid={`yt-inbox-redraft-dismiss-${channelSlot}`}
                  className="text-[11px] opacity-60 hover:opacity-100 shrink-0"
                  title="Dismiss"
                >×</button>
              )}
            </div>
          )}

          {/* Feb 2026 (Randy pt-yt-trickle) — Daily budget counter. Shows
              rolling 24-hour used / cap / remaining so creators aren't
              guessing whether Post ALL will fit. Only renders when
              connected + unlocked; hidden in preview / samples mode.
              Colour flips to a warning tint when < 10% remaining so
              a soon-to-be-hit cap is visible before it fires. */}
          {!showSamples && isConnected && isUnlocked && integration && (() => {
            const cap = Number(integration.max_posts_per_day) || 30;
            const used = Number(stats.postedToday) || 0;
            const remaining = Math.max(0, cap - used);
            const lowBudget = remaining <= Math.max(3, Math.floor(cap * 0.1));
            return (
              <div
                data-testid={`yt-inbox-daily-budget-${channelSlot}`}
                className={`mb-3 text-[10.5px] tracking-normal normal-case flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 rounded-md px-2.5 py-1.5 ${
                  lowBudget
                    ? "bg-[#FDF4EE]/70 border border-[#EBC9C9]/60 text-[#8A4844]"
                    : "bg-[#F4F8F5]/70 border border-[#B9D4BE]/40 text-[#4A5D4E]"
                }`}
                title="Rolling 24-hour post budget. Only applies when Auto-post modes are enabled OR when you use [Post ALL] / [Accept ALL]. Bump the cap in the Rate Limits card below."
              >
                <span className="whitespace-nowrap">
                  <span className="uppercase tracking-[0.14em] opacity-70 mr-1">Daily budget ·</span>
                  <span className="font-semibold tabular-nums" data-testid={`yt-inbox-daily-budget-used-${channelSlot}`}>{used}</span>
                  <span className="opacity-60"> / </span>
                  <span className="tabular-nums" data-testid={`yt-inbox-daily-budget-cap-${channelSlot}`}>{cap}</span>
                  <span className="opacity-60"> posted in last 24h</span>
                </span>
                <span className="tabular-nums whitespace-nowrap" data-testid={`yt-inbox-daily-budget-remaining-${channelSlot}`}>
                  {remaining} remaining
                </span>
              </div>
            );
          })()}

          {!showSamples && isConnected && drafts.length === 0 && initialLoaded && (
            <div className="border hairline rounded-lg bg-white p-6 text-center space-y-2" data-testid="yt-console-empty-state">
              <YoutubeLogo size={28} weight="duotone" className="text-[#B54848]/60 mx-auto" />
              <div className="text-[13px] font-medium text-[#1C1B1A]">All caught up.</div>
              <div className="text-[11.5px] text-[#6B6A68] max-w-sm mx-auto leading-relaxed">
                No question comments yet on your videos. Drafts appear here within ~90 seconds of a new question landing on any video you monitor. Or tap Refresh to check right now.
              </div>
            </div>
          )}

          {!showSamples && !isConnected && initialLoaded && (
            <div className="border hairline rounded-lg bg-white p-6 text-center space-y-2" data-testid="yt-console-notconnected-state">
              <YoutubeLogo size={28} weight="duotone" className="text-[#6B6A68]/50 mx-auto" />
              <div className="text-[13px] font-medium text-[#1C1B1A]">Not connected yet.</div>
              <div className="text-[11.5px] text-[#6B6A68] max-w-sm mx-auto leading-relaxed">
                Open the &quot;How does this work?&quot; setup pop-up on the card above and finish the 4-step Google connection to start drafting replies.
              </div>
            </div>
          )}

          {drafts.length > 0 && filteredDrafts.length === 0 && (
            <div className="border hairline rounded-lg bg-white p-6 text-center text-[12px] text-[#6B6A68]">
              No drafts match this filter. Switch the filter above or clear it to see all {drafts.length} row{drafts.length === 1 ? "" : "s"}.
            </div>
          )}

          {filteredDrafts.length > 0 && (
            <div className="space-y-3">
              {filteredDrafts.map((d) => {
                const isFocused = !!kwLower && kwMatchIds[matchCursor] === d.id;
                return (
                  <YTDraftCard
                    key={d.id}
                    draft={d}
                    siloId={siloId}
                    siloSlug={siloSlug}
                    testIdPrefix={`yt-draft-${slot.name.replace(" ", "").toLowerCase()}`}
                    onChanged={refresh}
                    highlightKeyword={kwLower}
                    isFocusedMatch={isFocused}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === "today" && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Posted today · {slot.name}</div>
          {todayLoading ? (
            <div className="text-[12px] text-[#6B6A68] italic p-4">Loading…</div>
          ) : todayRows.filter((d) => !locallyDismissed.has(d.id)).length === 0 ? (
            <div className="border hairline rounded-lg bg-white p-6 text-center text-[12px] text-[#6B6A68]">
              {todayRows.length === 0 ? `Nothing posted today yet on ${slot.name}.` : "All rows dismissed from this view. Tab away and back to restore."}
            </div>
          ) : (
            <div className="space-y-3">
              {todayRows.filter((d) => !locallyDismissed.has(d.id)).map((d) => (
                <YTDraftCard
                  key={d.id}
                  draft={d}
                  siloId={siloId}
                  siloSlug={siloSlug}
                  testIdPrefix={`yt-posted-today-${channelSlot}`}
                  onChanged={refresh}
                  isPosted
                  onLocalDismiss={dismissLocally}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {view === "history" && (
        <HistoryPanel siloId={siloId} siloSlug={siloSlug} channelSlot={channelSlot} slot={slot} isUnlocked={isUnlocked} refresh={refresh} />
      )}
    </div>
  );
}

function HistoryPanel({ siloId, siloSlug, channelSlot, slot, isUnlocked, refresh }) {
  const [range, setRange] = useState("3d");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Jul 30, 2026 (Randy pt89c-xxxii) — local dismiss; resets on load
  const [dismissed, setDismissed] = useState(new Set());
  const dismissLocally = (id) => setDismissed((s) => { const n = new Set(s); n.add(id); return n; });
  // Jul 30, 2026 (Randy pt89c-xxxv) — CLIENT-SIDE Q/NQ + keyword search
  // (previously server-side, which required a Load-button round-trip on
  // every change — Randy correctly called that broken UX). Load pulls the
  // full range once; filter + keyword slice it in-memory. Classical
  // search uses the same live-highlight + Prev/Next cursor pattern as
  // the Pending toolbar.
  const [filter, setFilter] = useState("all");
  const [kw, setKw] = useState("");
  const [matchCursor, setMatchCursor] = useState(0);

  const load = useCallback(async () => {
    if (!siloId || !isUnlocked) return;
    setLoading(true);
    setDismissed(new Set());
    try {
      // Client-side handles filter + keyword — always pull full range/all.
      const r = await ytApi.postedHistory(siloId, channelSlot, { range, filter: "all", q: "" });
      setRows((r.drafts || []).map(mapDraftRow));
      setLoaded(true);
    } catch { setRows([]); setLoaded(true); }
    finally { setLoading(false); }
  }, [siloId, isUnlocked, channelSlot, range]);

  // Auto-reload whenever the range chip changes (server-side round-trip
  // required — the backend range is what limits how far back we fetch).
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setMatchCursor(0); }, [kw, filter]);

  // Client-side filtered + keyword-matched rows
  const filteredByType = rows.filter((d) => {
    if (filter === "q" && d.isQuestion !== true) return false;
    if (filter === "nq" && d.isQuestion !== false) return false;
    return true;
  });
  const kwLower = kw.trim().toLowerCase();
  const kwMatchIds = kwLower
    ? filteredByType
        .filter((d) => (
          (d.commentText || "").toLowerCase().includes(kwLower) ||
          (d.draftReply || "").toLowerCase().includes(kwLower) ||
          (d.videoTitle || "").toLowerCase().includes(kwLower) ||
          (d.author || "").toLowerCase().includes(kwLower)
        ))
        .map((d) => d.id)
    : [];

  const RANGE_CHIPS = [
    { v: "today", label: "Today" },
    { v: "3d", label: "3 days" },
    { v: "7d", label: "7 days" },
    { v: "14d", label: "14 days" },
    { v: "30d", label: "30 days" },
  ];
  const FILTER_DOTS = [
    { v: "all", label: `All (${rows.length})` },
    { v: "q", label: `Questions (${rows.filter((r) => r.isQuestion === true).length})` },
    { v: "nq", label: `Non-questions (${rows.filter((r) => r.isQuestion === false).length})` },
  ];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2 flex items-center gap-2">
        <span>History · {slot.name}</span>
        {loading && <span className="text-[9px] normal-case tracking-normal italic text-[#4A5D4E]">Loading…</span>}
      </div>
      <div className="border hairline rounded-lg bg-white p-3 space-y-3">
        {/* Range chips (server round-trip on change) */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68] mr-1">Range:</span>
          {RANGE_CHIPS.map((c) => (
            <button
              key={c.v}
              onClick={() => setRange(c.v)}
              disabled={loading}
              data-testid={`yt-history-range-${channelSlot}-${c.v}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border hairline disabled:opacity-40 ${range === c.v ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-white text-[#1C1B1A] hover:bg-[#F0F1EE]"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Filter radios + classical search — all CLIENT-SIDE now.
            Randy pt89c-xxxv: no more Load-button dance on every change. */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68] mr-1">Filter:</span>
          {FILTER_DOTS.map((d) => (
            <label key={d.v} className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer">
              <input
                type="radio"
                name={`hist-filter-${channelSlot}`}
                checked={filter === d.v}
                onChange={() => setFilter(d.v)}
                data-testid={`yt-history-filter-${channelSlot}-${d.v}`}
              />
              <span>{d.label}</span>
            </label>
          ))}
          <div className="ml-auto flex items-center gap-1">
            <MagnifyingGlass size={11} weight="bold" className="text-[#6B6A68]" />
            <input
              type="text"
              placeholder="Search in view…"
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              data-testid={`yt-history-keyword-${channelSlot}`}
              className="text-[11px] px-2 py-1 border hairline rounded bg-white w-40 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            {kw && (
              <>
                <span className="text-[10px] text-[#6B6A68] tabular-nums">
                  {kwMatchIds.length === 0 ? "0 / 0" : `${matchCursor + 1} / ${kwMatchIds.length}`}
                </span>
                <button
                  onClick={() => setMatchCursor((c) => (kwMatchIds.length ? (c - 1 + kwMatchIds.length) % kwMatchIds.length : 0))}
                  disabled={kwMatchIds.length === 0}
                  data-testid={`yt-history-search-prev-${channelSlot}`}
                  className="text-[11px] w-6 h-6 rounded border hairline hover:bg-[#F0F1EE] disabled:opacity-30"
                  title="Previous match"
                >↑</button>
                <button
                  onClick={() => setMatchCursor((c) => (kwMatchIds.length ? (c + 1) % kwMatchIds.length : 0))}
                  disabled={kwMatchIds.length === 0}
                  data-testid={`yt-history-search-next-${channelSlot}`}
                  className="text-[11px] w-6 h-6 rounded border hairline hover:bg-[#F0F1EE] disabled:opacity-30"
                  title="Next match"
                >↓</button>
                <button
                  onClick={() => setKw("")}
                  data-testid={`yt-history-search-clear-${channelSlot}`}
                  className="text-[10px] text-[#6B6A68] hover:text-[#B54848]"
                  title="Clear search"
                >×</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3">
        {!loaded ? (
          <div className="border hairline rounded-lg bg-white p-6 text-center text-[12px] text-[#6B6A68] italic">
            Loading…
          </div>
        ) : (() => {
          const visibleRows = filteredByType.filter((d) => !dismissed.has(d.id));
          if (rows.length === 0) {
            return (
              <div className="border hairline rounded-lg bg-white p-6 text-center text-[12px] text-[#6B6A68]">
                No posted replies in this range.
              </div>
            );
          }
          if (visibleRows.length === 0) {
            return (
              <div className="border hairline rounded-lg bg-white p-6 text-center text-[12px] text-[#6B6A68]">
                {filter !== "all" ? `No ${filter === "q" ? "questions" : "non-questions"} in this range.` : "All rows dismissed from this view."}
              </div>
            );
          }
          return (
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68]">
                {visibleRows.length} result{visibleRows.length === 1 ? "" : "s"}
                {visibleRows.length !== filteredByType.length && (
                  <span className="ml-2 normal-case tracking-normal italic text-[#8A4844]">
                    ({filteredByType.length - visibleRows.length} dismissed locally)
                  </span>
                )}
              </div>
              {visibleRows.map((d) => {
                const isFocused = !!kwLower && kwMatchIds[matchCursor] === d.id;
                return (
                  <YTDraftCard
                    key={d.id}
                    draft={d}
                    siloId={siloId}
                    siloSlug={siloSlug}
                    testIdPrefix={`yt-history-${channelSlot}`}
                    onChanged={() => { refresh(); load(); }}
                    isPosted
                    onLocalDismiss={dismissLocally}
                    highlightKeyword={kwLower}
                    isFocusedMatch={isFocused}
                  />
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function SettingsTab({ safety, setSafety, videoFilter, setVideoFilter, signature, setSignature, confGateEnabled, setConfGateEnabled, confGateThreshold, setConfGateThreshold, isUnlocked, integration, persistSetting, channelSlot, siloId }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const monitoredCount = (integration?.monitored_video_ids || []).length;
  // Jul 30, 2026 (Randy pt89c-xxxi) — YT-Replies-only settings, one per
  // (silo × channel_slot). Never read by public QA.
  const findMode = integration?.find_comments_mode || "questions_only";
  const [lengthModal, setLengthModal] = useState(null); // "q" | "nq" | null
  const lengthQ = integration?.answer_length_questions || null;
  const lengthNQ = integration?.answer_length_non_questions || null;
  const LENGTH_LABEL = {
    one_liner: "One liner",
    one_paragraph: "One paragraph",
    concise: "Concise",
    standard: "Standard",
    deep_dive: "Deep dive",
  };
  const lengthLabel = (v) => v ? (LENGTH_LABEL[v] || v) : "Let the AI decide";
  return (
    <div className="space-y-5">
      {/* Find comments — top-most, above Safety mode per Randy */}
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Find comments</div>
        <div className="space-y-1.5">
          {[
            { v: "questions_only", label: "Questions only", hint: "Only comments the AI reads as a question get a drafted reply. Non-questions are auto-skipped." },
            { v: "all_comments", label: "All comments", hint: "Every incoming comment gets a drafted reply — greetings, praise, disputes, questions, everything." },
          ].map((m) => (
            <label key={m.v} className="flex items-start gap-2 p-2 rounded border hairline hover:bg-[#F0F1EE]/60 cursor-pointer">
              <input
                type="radio"
                name={`findmode-${channelSlot}`}
                checked={findMode === m.v}
                data-testid={`yt-console-findmode-${channelSlot}-${m.v}`}
                onChange={() => { if (isUnlocked && integration) persistSetting({ find_comments_mode: m.v }); }}
                className="mt-1"
              />
              <div className="min-w-0">
                <div className="text-[12px] text-[#1C1B1A] font-medium">{m.label}</div>
                <div className="text-[11px] text-[#6B6A68] mt-0.5">{m.hint}</div>
              </div>
            </label>
          ))}
        </div>
        {/* Feb 2026 (Randy pt-yt-reply-to-reply) — indented sub-toggle
            under "Find comments". Opt-in for depth=2 nested replies:
            when a fan replies to OUR posted reply, draft a follow-up
            (Q2/NQ2). Same safety mode as depth-1 (Randy's Option A).
            Default OFF — creators must explicitly enable per channel. */}
        <div className="mt-2 ml-6 pl-3 border-l-2 border-[#E4E1DB]">
          <label className="flex items-start gap-2 p-2 rounded border hairline hover:bg-[#F0F1EE]/60 cursor-pointer">
            <input
              type="checkbox"
              checked={!!integration?.reply_to_replies_enabled}
              data-testid={`yt-console-reply-to-replies-${channelSlot}`}
              disabled={!isUnlocked || !integration}
              onChange={(e) => {
                if (isUnlocked && integration) persistSetting({ reply_to_replies_enabled: e.target.checked });
              }}
              className="mt-1"
            />
            <div className="min-w-0">
              <div className="text-[12px] text-[#1C1B1A] font-medium flex items-center gap-1.5">
                <span>Also draft replies to replies</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#EDE7DF] text-[#6B5A3E] uppercase tracking-[0.14em]">Depth&nbsp;2</span>
              </div>
              <div className="text-[11px] text-[#6B6A68] mt-0.5">
                When a fan replies to a reply your AI already posted, draft a follow-up (tagged <em className="not-italic">Q2</em>/<em className="not-italic">NQ2</em>). Uses the same Safety mode as depth-1. YouTube gives us up to 5 nested replies per thread free of quota.
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Answer Length — one setting per comment type, per channel, per silo */}
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Answer length</div>
        <div className="text-[10.5px] text-[#6B6A68] mb-2">
          Applies only to replies posted from THIS YT Replies module on {channelSlot === "channel1" ? "Channel 1" : "Channel 2"}. Your public QA length is separate and unaffected.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setLengthModal("q")}
            data-testid={`yt-console-length-q-${channelSlot}`}
            className="border hairline rounded-md p-2.5 bg-white hover:bg-[#F0F1EE]/60 text-left"
          >
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68]">Questions</div>
            <div className="text-[13px] font-medium text-[#1C1B1A]">{lengthLabel(lengthQ)}</div>
          </button>
          <button
            onClick={() => setLengthModal("nq")}
            data-testid={`yt-console-length-nq-${channelSlot}`}
            className="border hairline rounded-md p-2.5 bg-white hover:bg-[#F0F1EE]/60 text-left"
          >
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68]">Non Questions</div>
            <div className="text-[13px] font-medium text-[#1C1B1A]">{lengthLabel(lengthNQ)}</div>
          </button>
        </div>
      </div>

      {lengthModal && (
        <LengthPickerModal
          type={lengthModal}
          current={lengthModal === "q" ? lengthQ : lengthNQ}
          onClose={() => setLengthModal(null)}
          onPick={(v) => {
            const patch = lengthModal === "q"
              ? { answer_length_questions: v || "" }
              : { answer_length_non_questions: v || "" };
            persistSetting(patch);
            setLengthModal(null);
          }}
          channelSlot={channelSlot}
        />
      )}

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Safety mode</div>
        <div className="space-y-1.5">
          {SAFETY_MODES.map((m) => (
            <label key={m.v} className="flex items-start gap-2 p-2 rounded border hairline hover:bg-[#F0F1EE]/60 cursor-pointer">
              <input type="radio" name={`safety-${channelSlot}`} checked={safety === m.v}
                data-testid={`yt-console-safety-${channelSlot}-${m.v}`}
                onChange={() => { setSafety(m.v); if (isUnlocked && integration) persistSetting({ safety_mode: m.v }); }} className="mt-1" />
              <div className="min-w-0">
                <div className="text-[12px] text-[#1C1B1A] font-medium">{m.label}</div>
                <div className="text-[11px] text-[#6B6A68] mt-0.5">{m.hint}</div>
              </div>
            </label>
          ))}
        </div>

        {/* Feb 2026 (Randy pt-yt-conf-gate) — confidence-gate safety net.
            Sits inside the Safety mode section (visually a sub-panel) so
            the relationship is obvious. Only meaningful when safety_mode
            is an auto mode; on `draft` we grey it out but still let the
            creator configure it so switching to auto later feels ready.
            Slot: BELOW the three radio modes, ABOVE "Which videos it
            monitors" (per Randy's exact spec). */}
        <div className="mt-3 p-3 rounded-md border hairline bg-[#FDFBF6]"
             data-testid={`yt-console-conf-gate-${channelSlot}`}>
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id={`conf-gate-toggle-${channelSlot}`}
              checked={confGateEnabled}
              data-testid={`yt-console-conf-gate-toggle-${channelSlot}`}
              onChange={(e) => {
                const v = e.target.checked;
                setConfGateEnabled(v);
                if (isUnlocked && integration) persistSetting({ confidence_gate_enabled: v });
              }}
              className="mt-1"
            />
            <label htmlFor={`conf-gate-toggle-${channelSlot}`} className="min-w-0 flex-1 cursor-pointer">
              <div className="text-[12px] text-[#1C1B1A] font-medium">
                Confidence gate on auto-post
                <span className="ml-1.5 text-[10px] uppercase tracking-[0.14em] text-[#7A8A7C]">safety net</span>
              </div>
              <div className="text-[11px] text-[#6B6A68] mt-0.5">
                Auto-post only when a draft&apos;s confidence ≥ threshold. Confidence is the real retrieval evidence behind that draft — how strongly your own library matched the comment — and any draft whose text admits it doesn&apos;t know scores 0. Lower-confidence drafts stay pending regardless of rate-limit budget. Applies to full-auto pathways only (poller + backfill). No effect on Draft mode.
              </div>
            </label>
          </div>
          {confGateEnabled && (
            <div className="mt-2.5 pl-6 flex items-center gap-2 flex-wrap">
              <span className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68]">Threshold</span>
              <input
                type="range"
                min="0.30"
                max="0.90"
                step="0.01"
                value={confGateThreshold}
                data-testid={`yt-console-conf-gate-slider-${channelSlot}`}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setConfGateThreshold(v);
                }}
                onMouseUp={() => { if (isUnlocked && integration) persistSetting({ confidence_gate_threshold: confGateThreshold }); }}
                onTouchEnd={() => { if (isUnlocked && integration) persistSetting({ confidence_gate_threshold: confGateThreshold }); }}
                className="flex-1 min-w-[140px] accent-[#4A5D4E]"
              />
              <span
                data-testid={`yt-console-conf-gate-value-${channelSlot}`}
                className="text-[13px] font-medium text-[#1C1B1A] tabular-nums w-[52px] text-right"
              >
                {Math.round(confGateThreshold * 100)}%
              </span>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Which videos it monitors</div>
        <div className="space-y-1.5">
          {FILTER_MODES.map((m) => (
            <div key={m.v} className="flex items-start gap-2 p-2 rounded border hairline hover:bg-[#F0F1EE]/60">
              <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-0">
                <input type="radio" name={`filter-${channelSlot}`} checked={videoFilter === m.v}
                  data-testid={`yt-console-filter-${channelSlot}-${m.v}`}
                  onChange={() => { setVideoFilter(m.v); if (isUnlocked && integration) persistSetting({ video_filter_mode: m.v }); }} className="mt-1" />
                <div className="text-[12px] text-[#1C1B1A]">{m.label}</div>
              </label>
              {/* Feb 2026 (Randy pt-yt-edit-selected-inline · iter164) —
                  "Edit selected videos (N)" now renders INLINE on the
                  "Only videos I select" pill row (right of the label,
                  same row as the dot), so the (N) count intuitively
                  ties to the dot Randy chose. Was previously stranded
                  below the three-pill group as a separate block. */}
              {m.v === "selected" && videoFilter === "selected" && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPickerOpen(true); }}
                  disabled={!isUnlocked || !integration}
                  data-testid={`yt-console-open-video-picker-${channelSlot}`}
                  className="text-[11px] px-2.5 py-1 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] disabled:opacity-40 shrink-0">
                  {monitoredCount > 0 ? `Edit selected videos (${monitoredCount})` : "Select videos…"}
                </button>
              )}
            </div>
          ))}
        </div>
        {videoFilter === "selected" && monitoredCount === 0 && (
          <div className="mt-2 pl-6 text-[11px] text-[#B54848] italic">
            ↑ No videos selected yet — the ambient poller has nothing to watch.
          </div>
        )}
        {videoFilter === "off" && (
          <div className="mt-2 pl-6 text-[11px] text-[#6B6A68] italic">
            Ambient poller is paused. No new drafts will appear until you switch back to option a or b.
          </div>
        )}
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Reply signature</div>
        {/* Jul 30, 2026 (Randy pt89c-xxxvii) — textarea instead of input so
            creators can hit Enter and break the signature across multiple
            lines. Backend accepts and preserves newlines; the sig-append
            logic (approve_draft) already handles multi-line signatures
            correctly since it uses `text_to_post = f"{text_to_post.rstrip()}\\n\\n{resolved_sig}"`. */}
        <textarea
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          onBlur={() => isUnlocked && integration && persistSetting({ reply_signature: signature })}
          data-testid={`yt-console-sig-input-${channelSlot}`}
          rows={3}
          className="w-full bg-white border hairline rounded-md px-3 py-2 text-[12px] font-mono focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] resize-y"
        />
        <p className="text-[10.5px] text-[#6B6A68] mt-1.5 leading-relaxed">
          Use <code className="bg-[#F0F1EE] px-1 rounded">{"{silo_name}"}</code> and <code className="bg-[#F0F1EE] px-1 rounded">{"{silo_url}"}</code> — signature can be edited but not deleted (Google TOS require AI-content disclosure). Multi-line supported (Enter drops to the next line).
        </p>
      </div>

      {/* Oct 2026 (Randy pt-yt-thread-limit) — the conversation cap, placed
          directly under Reply signature at his request, and shared by both
          channels wherever this console appears (Workbench + Refinery).
          Counted PER PERSON, PER THREAD: a newcomer who walks into a long
          thread still gets a real answer; the person who has already had
          their allowance gets the hand-off. Off = exactly as before. */}
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Response limit · candid replies</div>
        <label className="flex items-start gap-2 p-2 rounded border hairline hover:bg-[#F0F1EE]/60 cursor-pointer">
          <input
            type="checkbox"
            checked={integration?.thread_limit_enabled !== false}
            data-testid={`yt-console-thread-limit-${channelSlot}`}
            disabled={!isUnlocked || !integration}
            onChange={(e) => {
              if (isUnlocked && integration) persistSetting({ thread_limit_enabled: e.target.checked });
            }}
            className="mt-1"
          />
          <div className="min-w-0">
            <div className="text-[12px] text-[#1C1B1A] font-medium">Cap how many times the twin answers one person on a thread</div>
            <div className="text-[11px] text-[#6B6A68] mt-0.5">
              After their allowance, that person gets up to two candid replies pointing them at your silo, then the twin goes quiet on them. Everyone else on the thread is unaffected. Turn this off and every follow-up gets answered, with no limit on how long one conversation runs.
            </div>
          </div>
        </label>

        {integration?.thread_limit_enabled !== false && (
          <div className="mt-2 ml-6 pl-3 border-l-2 border-[#E4E1DB] space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-[#6B6A68]">Real answers per person, per thread</span>
              <select
                value={integration?.max_ai_replies_per_person || 3}
                data-testid={`yt-console-replies-per-person-${channelSlot}`}
                disabled={!isUnlocked || !integration}
                onChange={(e) => {
                  if (isUnlocked && integration) persistSetting({ max_ai_replies_per_person: Number(e.target.value) });
                }}
                className="bg-white border hairline rounded-md px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-[11px] text-[#6B6A68]">then the candid replies</span>
            </div>

            {[1, 2].map((n) => (
              <div key={n}>
                <div className="text-[11px] text-[#1C1B1A] mb-1">
                  Candid reply {n} <span className="text-[#6B6A68]">· {n === 1 ? "sent once their answers run out" : "the last thing the twin says on that thread"}</span>
                </div>
                <textarea
                  defaultValue={integration?.[`limit_notice_${n}`] || ""}
                  onBlur={(e) => {
                    if (isUnlocked && integration) persistSetting({ [`limit_notice_${n}`]: e.target.value });
                  }}
                  data-testid={`yt-console-limit-notice-${n}-${channelSlot}`}
                  disabled={!isUnlocked || !integration}
                  rows={2}
                  className="w-full bg-white border hairline rounded-md px-3 py-2 text-[12px] font-mono focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] resize-y disabled:opacity-60"
                />
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-[#6B6A68]">Most candid replies shown on one thread</span>
              <select
                value={integration?.max_limit_notices_per_thread || 2}
                data-testid={`yt-console-notices-per-thread-${channelSlot}`}
                disabled={!isUnlocked || !integration}
                onChange={(e) => {
                  if (isUnlocked && integration) persistSetting({ max_limit_notices_per_thread: Number(e.target.value) });
                }}
                className="bg-white border hairline rounded-md px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              >
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <p className="text-[10.5px] text-[#6B6A68] leading-relaxed">
              Both use <code className="bg-[#F0F1EE] px-1 rounded">{"{silo_name}"}</code> and <code className="bg-[#F0F1EE] px-1 rounded">{"{silo_url}"}</code>. Candid replies cost nothing to produce — no AI is spent on them — but they follow your Safety mode: on full auto they post themselves, on draft-only they wait in your inbox badged <em className="not-italic">Final reply · limit</em>. Re-drafting one there is treated as your decision to carry on, and gives that person one more real answer.
            </p>
          </div>
        )}
      </div>

      {pickerOpen && (
        <YTVideoPickerModal
          siloId={siloId}
          channelSlot={channelSlot}
          initialSelected={integration?.monitored_video_ids || []}
          onClose={() => setPickerOpen(false)}
          onSaved={(ids) => { setPickerOpen(false); persistSetting({ monitored_video_ids: ids }); }}
        />
      )}
    </div>
  );
}

function AnalyticsTab({ stats }) {
  return (
    <div className="border hairline rounded-lg bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-3 flex items-center gap-1.5">
        <ChartLineUp size={11} weight="duotone" /> Last 7 days
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
        {[
          { k: "Replies", v: stats.postedToday * 3 },
          { k: "Clicks", v: stats.clicksThisWeek },
          { k: "New silo visits", v: Math.round(stats.clicksThisWeek * 0.62) },
          { k: "LLM spend", v: `$${(stats.postedToday * 3 * 0.019).toFixed(2)}` },
        ].map((s) => (
          <div key={s.k}>
            <div className="text-[18px] font-medium text-[#1C1B1A]">{s.v}</div>
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#6B6A68] mt-0.5">{s.k}</div>
          </div>
        ))}
      </div>
      <p className="text-[10.5px] text-[#6B6A68] mt-3 italic">Real-time metrics update as your fans click through the signature link back to your silo.</p>
    </div>
  );
}

function DangerTab({ integration, busy, isUnlocked, doTogglePause, doDisconnect, channelSlot }) {
  return (
    <div className="space-y-2">
      <button onClick={() => isUnlocked ? doTogglePause() : toast.info("Unlock first.")}
        disabled={busy}
        data-testid={`yt-console-pause-${channelSlot}`}
        className="w-full text-left p-3 rounded-md border border-[#EBC9C9] bg-[#FDECEC]/40 hover:bg-[#FDECEC] flex items-center gap-2 text-[12px] text-[#B54848] disabled:opacity-50">
        <Pause size={14} weight="duotone" /> <span className="font-medium">{integration?.paused ? "Resume replies" : "Pause all replies"}</span>
        <span className="ml-auto text-[10.5px] text-[#8A4844]">Instant kill switch · 1 tap</span>
      </button>
      <button onClick={() => isUnlocked ? doDisconnect() : toast.info("Unlock first.")}
        disabled={busy}
        data-testid={`yt-console-disconnect-${channelSlot}`}
        className="w-full text-left p-3 rounded-md border hairline hover:bg-[#F0F1EE] flex items-center gap-2 text-[12px] text-[#3A3A38] disabled:opacity-50">
        <Plugs size={14} weight="duotone" /> <span className="font-medium">Disconnect YouTube</span>
        <span className="ml-auto text-[10.5px] text-[#6B6A68]">Revokes token · doesn&apos;t delete history</span>
      </button>
      <div className="border-t hairline pt-3 mt-2 text-[10.5px] text-[#6B6A68] italic flex items-center justify-between">
        <span>One SKU covers Channel 1 + Channel 2</span>
        <button onClick={() => toast.info("Opens the how-it-works modal on the locked card above.")}
          className="text-[#2C6E92] hover:text-[#1B4E6E] hover:underline font-medium inline-flex items-center gap-1">
          Re-read setup <ArrowRight size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
}


// ─── Length Picker Modal ────────────────────────────────────────────
// Jul 30, 2026 (Randy pt89c-xxxi) — Small in-page modal for picking the
// answer-length preset per comment type (Questions vs Non-Questions).
// One picker per channel slot, per silo. Never touches public QA.
function LengthPickerModal({ type, current, onClose, onPick, channelSlot }) {
  // Jul 30, 2026 (Randy pt89c-xxxiii) — separate copy per Q vs NQ. NQ
  // options include a strong "thank-you-for-commenting" tonal nudge so
  // the reply matches the commenter's demeanor rather than teaching.
  // (The tonal nudge is ALSO applied server-side inside _draft_reply
  // via a prepended stance preamble on the ask_silo payload — this UI
  // copy is what Randy sees when picking; the LLM enforcement is
  // downstream.)
  const OPTIONS = type === "nq" ? [
    { v: "", label: "Let the AI decide", hint: "Model picks the tone and length. Still enforces the 'react-don't-teach' stance — receive, thank, or engage, but never lecture." },
    { v: "one_liner", label: "One liner", hint: "One warm sentence (~10-30 words). A friendly acknowledgment. NO teaching, NO product plugs. Match their vibe: thanks → thank back, praise → receive graciously, disagreement → engage respectfully in a single line." },
    { v: "one_paragraph", label: "One paragraph", hint: "One tight paragraph (~40-80 words). Warm human reaction. Acknowledge what they said, mirror their energy, maybe add ONE line of personal context. STILL no teaching, no solving, no links unless they asked." },
    { v: "concise", label: "Concise", hint: "~150-300 words. Deeper thank-you-style engagement — you may add a personal story or an anecdote that RELATES to their comment, but keep it conversational and reactive. NOT a tutorial." },
    { v: "standard", label: "Standard", hint: "~400-700 words. Long-form acknowledgment — appropriate for very meaningful comments where a real conversation is warranted. Still reactive, not instructive." },
    { v: "deep_dive", label: "Deep dive", hint: "Uncapped length — use this only for deep-topic comments where the commenter clearly wants a long back-and-forth. The 'react not teach' stance still applies." },
  ] : [
    { v: "", label: "Let the AI decide", hint: "Model picks length based on the question — 1 sentence for simple asks, several paragraphs for a real deep-dive." },
    { v: "one_liner", label: "One liner", hint: "STRICT one sentence (~10-30 words). Direct answer only. NO preamble, NO closing, NO caveats. Best for quick clarifications." },
    { v: "one_paragraph", label: "One paragraph", hint: "STRICT single paragraph (~40-80 words). Punchy answer. Feels like a real YouTube reply — get to the point fast." },
    { v: "concise", label: "Concise", hint: "~150-300 words (2-3 short paragraphs). Enough for a real answer without a wall of text." },
    { v: "standard", label: "Standard", hint: "~400-700 words (3-5 paragraphs). Medium depth. Headings allowed if they aid clarity." },
    { v: "deep_dive", label: "Deep dive", hint: "800-2000+ words. Long, thorough — for creators who want their AI to genuinely educate in the comments." },
  ];
  const title = type === "q" ? "Answer length · Questions" : "Answer length · Non-Questions";
  const channelName = channelSlot === "channel1" ? "Channel 1" : "Channel 2";
  const [picked, setPicked] = useState(current || "");
  const handleSave = () => onPick(picked || null);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3 cta-safe"
      data-testid={`yt-length-modal-${channelSlot}-${type}`}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#FDFCF8] border hairline rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b hairline bg-white/60">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] font-medium">{channelName}</div>
          <div className="text-[14px] font-medium text-[#1C1B1A] mt-0.5">{title}</div>
        </div>
        <div className="p-4 space-y-1.5 max-h-[60vh] overflow-y-auto">
          {OPTIONS.map((o) => (
            <label
              key={o.v || "default"}
              className={`flex items-start gap-2 p-2.5 rounded border hairline cursor-pointer transition ${picked === o.v ? "bg-[#F4F3EE] border-[#4A5D4E]/40" : "bg-white hover:bg-[#F0F1EE]/60"}`}
            >
              <input
                type="radio"
                name={`length-${channelSlot}-${type}`}
                checked={picked === o.v}
                onChange={() => setPicked(o.v)}
                data-testid={`yt-length-opt-${channelSlot}-${type}-${o.v || "default"}`}
                className="mt-1"
              />
              <div className="min-w-0">
                <div className="text-[12.5px] text-[#1C1B1A] font-medium">{o.label}</div>
                <div className="text-[11px] text-[#6B6A68] mt-0.5">{o.hint}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="px-4 py-3 border-t hairline bg-white/60 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid={`yt-length-cancel-${channelSlot}-${type}`}
            className="text-[12px] px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            data-testid={`yt-length-save-${channelSlot}-${type}`}
            className="text-[12px] px-4 py-1.5 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E]"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
